// src/services/contactMessageService.ts
import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const CONTACT_MESSAGES_COLLECTION = 'contactMessages';

export interface ContactMessage {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  whatsapp?: string;
  message: string;
  status: 'read' | 'unread';
  createdAt: Date;
}

export interface PaginatedMessagesResult {
  messages: ContactMessage[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export class ContactMessageService {
  /**
   * Get paginated contact messages — uses cursor-seek so any page can be
   * fetched in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   */
  static async getMessagesPaginated(
    pageSize: number = 10,
    page: number = 1,
    statusFilter?: 'read' | 'unread',
    searchQuery?: string
  ): Promise<PaginatedMessagesResult> {
    try {
      const messages: ContactMessage[] = [];
      const collectionRef = collection(db, CONTACT_MESSAGES_COLLECTION);

      // Build where-constraints (shared between count + seek + page queries)
      const whereConstraints: any[] = [];

      if (statusFilter) {
        whereConstraints.push(where('status', '==', statusFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...whereConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...whereConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...whereConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...whereConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const message: ContactMessage = {
          id: doc.id,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          whatsapp: data.whatsapp || '',
          message: data.message || '',
          status: data.status || 'unread',
          createdAt: data.createdAt?.toDate() || new Date(),
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            message.name.toLowerCase().includes(searchLower) ||
            message.email.toLowerCase().includes(searchLower) ||
            message.message.toLowerCase().includes(searchLower) ||
            (message.phone || '').toLowerCase().includes(searchLower) ||
            (message.whatsapp || '').toLowerCase().includes(searchLower);

          if (matchesSearch) {
            messages.push(message);
          }
        } else {
          messages.push(message);
        }
      });

      return {
        messages,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error: any) {
      console.error('Error fetching paginated messages:', error);
      throw error;
    }
  }

  /**
   * Get all contact messages (deprecated - use getMessagesPaginated instead)
   */
  static async getMessages(): Promise<ContactMessage[]> {
    try {
      const messagesRef = collection(db, CONTACT_MESSAGES_COLLECTION);
      const q = query(messagesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        email: doc.data().email,
        phone: doc.data().phone || '',
        whatsapp: doc.data().whatsapp || '',
        message: doc.data().message,
        status: doc.data().status || 'unread',
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as ContactMessage[];

      return messages;
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      throw error;
    }
  }

  /**
   * Get unread messages only
   */
  static async getUnreadMessages(): Promise<ContactMessage[]> {
    try {
      const messagesRef = collection(db, CONTACT_MESSAGES_COLLECTION);
      const q = query(
        messagesRef,
        where('status', '==', 'unread'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        email: doc.data().email,
        phone: doc.data().phone || '',
        whatsapp: doc.data().whatsapp || '',
        message: doc.data().message,
        status: doc.data().status || 'unread',
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as ContactMessage[];

      return messages;
    } catch (error) {
      console.error('Error fetching unread messages:', error);
      throw error;
    }
  }

  /**
   * Get read messages only
   */
  static async getReadMessages(): Promise<ContactMessage[]> {
    try {
      const messagesRef = collection(db, CONTACT_MESSAGES_COLLECTION);
      const q = query(
        messagesRef,
        where('status', '==', 'read'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);

      const messages = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || '',
        email: doc.data().email,
        phone: doc.data().phone || '',
        whatsapp: doc.data().whatsapp || '',
        message: doc.data().message,
        status: doc.data().status || 'unread',
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as ContactMessage[];

      return messages;
    } catch (error) {
      console.error('Error fetching read messages:', error);
      throw error;
    }
  }

  /**
   * Get single message by ID
   */
  static async getMessage(messageId: string): Promise<ContactMessage | null> {
    try {
      const messageRef = doc(db, CONTACT_MESSAGES_COLLECTION, messageId);
      const messageSnap = await getDoc(messageRef);

      if (messageSnap.exists()) {
        const data = messageSnap.data();
        return {
          id: messageSnap.id,
          name: data.name || '',
          email: data.email,
          phone: data.phone || '',
          whatsapp: data.whatsapp || '',
          message: data.message,
          status: data.status || 'unread',
          createdAt: data.createdAt?.toDate() || new Date(),
        } as ContactMessage;
      }

      return null;
    } catch (error) {
      console.error('Error fetching message:', error);
      throw error;
    }
  }

  /**
   * Update message status
   */
  static async updateMessageStatus(
    messageId: string,
    status: 'read' | 'unread'
  ): Promise<void> {
    try {
      const messageRef = doc(db, CONTACT_MESSAGES_COLLECTION, messageId);
      await updateDoc(messageRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating message status:', error);
      throw error;
    }
  }

  /**
   * Mark message as read
   */
  static async markAsRead(messageId: string): Promise<void> {
    try {
      await this.updateMessageStatus(messageId, 'read');
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  }

  /**
   * Mark message as unread
   */
  static async markAsUnread(messageId: string): Promise<void> {
    try {
      await this.updateMessageStatus(messageId, 'unread');
    } catch (error) {
      console.error('Error marking message as unread:', error);
      throw error;
    }
  }

  /**
   * Delete message
   */
  static async deleteMessage(messageId: string): Promise<void> {
    try {
      const messageRef = doc(db, CONTACT_MESSAGES_COLLECTION, messageId);
      await deleteDoc(messageRef);
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  }

  /**
   * Get message count by status
   */
  static async getMessageCount(): Promise<{
    total: number;
    read: number;
    unread: number;
  }> {
    try {
      const collectionRef = collection(db, CONTACT_MESSAGES_COLLECTION);

      const [totalSnapshot, readSnapshot, unreadSnapshot] = await Promise.all([
        getCountFromServer(collectionRef),
        getCountFromServer(query(collectionRef, where('status', '==', 'read'))),
        getCountFromServer(query(collectionRef, where('status', '==', 'unread'))),
      ]);

      return {
        total: totalSnapshot.data().count,
        read: readSnapshot.data().count,
        unread: unreadSnapshot.data().count,
      };
    } catch (error) {
      console.error('Error getting message count:', error);
      throw error;
    }
  }
}